import React from 'react';

import classNames from 'classnames';

// valid html tags for headings (level of the heading in the page)
export enum HeadingTag {
  H1 = 'h1',
  H2 = 'h2',
  H3 = 'h3',
  H4 = 'h4',
  H5 = 'h5',
  H6 = 'h6',
}

// valid "business" sizes for headings to keep consistent with the original approach
export enum HeadingSize {
  HERO = 'text-h-x3 md:text-h-x1',
  H1 = 'text-h-x3 md:text-h-x2',
  H2 = 'text-h-x4 md:text-h-x3',
  H3 = 'text-h-x5 md:text-h-x4',
  H4 = 'text-h-x6 md:text-h-x6',
}

export type HeadingProps = {
  tag: HeadingTag;
  text: string | React.ReactNode;
  size?: HeadingSize | '';
  classes?: string;
  role?: string;
  align?: 'left' | 'center' | 'right';
  isFullWidth?: boolean;
  hoverEffect?: boolean;
  'data-testid'?: string;
};

const containsHTML = (str: string): boolean => /<\/?[a-z][\s\S]*>/i.test(str);

const Heading = ({
  tag,
  text,
  size = '',
  classes = '',
  role = 'heading',
  align,
  isFullWidth = true,
  hoverEffect,
  ['data-testid']: testId,
}: HeadingProps) => {
  const hasHTML = typeof text === 'string' && containsHTML(text);

  const baseProps = {
    role,
    className: classNames('font-bold', {
      [size]: size,
      [classes]: classes,
      [`text-${align}`]: align,
      'title-container': align === 'center',
      'w-full': isFullWidth,
      'text-charcoal-v2': !hoverEffect,
    }),
    'data-testid': testId || `heading-${tag}`,
  };

  if (hasHTML) {
    const props = { ...baseProps, dangerouslySetInnerHTML: { __html: text } };

    return React.createElement(tag, props);
  }

  return React.createElement(tag, baseProps, text);
};

export default React.memo(Heading);
